import { useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { User, Bot, ChevronDown, ChevronRight, FileText, ExternalLink } from "lucide-react";
import DocumentViewerModal from "./DocumentViewerModal";
import "./MessageBubble.css";

const DOCUMENT_TYPES_LABELS = {
    bando_di_gara: "Bando di Gara",
    disciplinare_tecnico: "Disciplinare Tecnico",
    allegati_disciplinare: "Allegati al Disciplinare",
    schema_di_contratto: "Schema di Contratto",
    pronunciamenti_aters: "Pronunciamenti ATERSIR",
    offerta_tecnica_rti: "Offerta Tecnica RTI",
    allegati_offerta: "Allegati Offerta",
    generico: "Documento Generico",
};

// Normalizza math delimiters e bold-fica i riferimenti strutturali. Stesso del POC.
function preprocessContent(text) {
    let result = text.replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`);
    result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => `$${inner}$`);

    const refs = [
        /(?<!\*\*)\b(Art\.?\s*\d+)/g,
        /(?<!\*\*)\b(CAPO\s+[IVXLCDM]+)/g,
        /(?<!\*\*)\b(Sezione\s+[A-Z](?:\.\d+)?)/gi,
        /(?<!\*\*)\b(punto\s+\d+(?:\.\d+)*)/gi,
        /(?<!\*\*)\b(comma\s+\d+)/gi,
        /(?<!\*\*)\b(lettera\s+[a-z]\))/gi,
    ];
    for (const re of refs) {
        result = result.replace(re, (match) => `**${match}**`);
    }

    // Estrai marker [N] dai blocchi **...** cosi' rendono come badge separati.
    result = result.replace(/\*\*([^*]*?)((?:\s*\[\d+\])+)\*\*/g, (_m, t, c) => `**${t.trimEnd()}**${c}`);

    return result;
}

// Trasforma marker [N] in <span> cliccabili. Stesso del POC.
function styleCitations(children, onCitationClick) {
    if (typeof children === "string") {
        const parts = children.split(/(\[\d+\])/g);
        if (parts.length === 1) return children;
        return parts.map((part, i) => {
            const match = part.match(/^\[(\d+)\]$/);
            if (match) {
                const idx = parseInt(match[1], 10);
                return (
                    <span
                        key={i}
                        className="rag-bubble__citation"
                        onClick={() => onCitationClick(idx)}
                    >
                        {part}
                    </span>
                );
            }
            return part;
        });
    }
    if (Array.isArray(children)) {
        return children.map((child, i) => {
            if (typeof child === "string") {
                const styled = styleCitations(child, onCitationClick);
                return Array.isArray(styled)
                    ? styled.map((s, j) => <span key={`${i}-${j}`}>{s}</span>)
                    : styled;
            }
            return child;
        });
    }
    return children;
}

function SourceItem({ source, expanded, onToggle, onOpenDocument }) {
    const docLabel =
        source.documentType && source.documentType in DOCUMENT_TYPES_LABELS
            ? DOCUMENT_TYPES_LABELS[source.documentType]
            : null;

    return (
        <div className="rag-bubble__source">
            <button onClick={onToggle} className="rag-bubble__source-header">
                <span className="rag-bubble__source-chevron">
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <div className="rag-bubble__source-meta">
                    <div className="rag-bubble__source-title">
                        <span className="rag-bubble__source-index">[{source.index}]</span>
                        <FileText size={11} className="rag-bubble__source-icon" />
                        {docLabel ? (
                            <>
                                <span className="rag-bubble__source-label">{docLabel}</span>
                                <span className="rag-bubble__source-sep">—</span>
                                <span className="rag-bubble__source-filename">{source.filename}</span>
                            </>
                        ) : (
                            <span className="rag-bubble__source-filename">{source.filename}</span>
                        )}
                    </div>
                    {!expanded && source.preview && (
                        <p className="rag-bubble__source-preview">{source.preview}</p>
                    )}
                </div>
            </button>
            {expanded && (
                <div className="rag-bubble__source-body">
                    <pre className="rag-bubble__source-content">{source.content}</pre>
                    <div className="rag-bubble__source-actions">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenDocument(source);
                            }}
                            className="rag-bubble__source-open"
                        >
                            <ExternalLink size={12} />
                            <span>Apri documento</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SourcesList({ sources, expandedSet, onToggle, onOpenDocument }) {
    if (!sources || sources.length === 0) return null;

    return (
        <div className="rag-bubble__sources">
            <p className="rag-bubble__sources-title">Fonti:</p>
            {sources.map((source) => (
                <SourceItem
                    key={source.index}
                    source={source}
                    expanded={expandedSet.has(source.index)}
                    onToggle={() => onToggle(source.index)}
                    onOpenDocument={onOpenDocument}
                />
            ))}
        </div>
    );
}

export default function MessageBubble({ message, isStreaming }) {
    const isUser = message.role === "user";
    const [expandedSources, setExpandedSources] = useState(new Set());
    const [viewerSource, setViewerSource] = useState(null);

    const toggleSource = useCallback((index) => {
        setExpandedSources((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    }, []);

    const mdComponents = {
        p: ({ children }) => <p>{styleCitations(children, toggleSource)}</p>,
        li: ({ children }) => <li>{styleCitations(children, toggleSource)}</li>,
        strong: ({ children }) => <strong>{styleCitations(children, toggleSource)}</strong>,
        table: ({ children }) => (
            <div className="rag-bubble__table-wrap">
                <table className="rag-bubble__table">{children}</table>
            </div>
        ),
        thead: ({ children }) => <thead className="rag-bubble__thead">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="rag-bubble__tr">{children}</tr>,
        th: ({ children }) => <th className="rag-bubble__th">{styleCitations(children, toggleSource)}</th>,
        td: ({ children }) => <td className="rag-bubble__td">{styleCitations(children, toggleSource)}</td>,
    };

    return (
        <div className={`rag-bubble ${isUser ? "rag-bubble--user" : "rag-bubble--assistant"}`}>
            <div className={`rag-bubble__avatar ${isUser ? "rag-bubble__avatar--user" : "rag-bubble__avatar--assistant"}`}>
                {isUser ? <User size={16} /> : <Bot size={16} />}
            </div>

            <div className={`rag-bubble__content ${isUser ? "rag-bubble__content--user" : "rag-bubble__content--assistant"}`}>
                {isUser ? (
                    <p className="rag-bubble__user-text">{message.content}</p>
                ) : (
                    <div className="rag-bubble__markdown">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={mdComponents}
                        >
                            {preprocessContent(message.content || "")}
                        </ReactMarkdown>
                        {isStreaming && <span className="rag-bubble__cursor" />}
                    </div>
                )}

                {message.sources && message.sources.length > 0 && (
                    <SourcesList
                        sources={message.sources}
                        expandedSet={expandedSources}
                        onToggle={toggleSource}
                        onOpenDocument={setViewerSource}
                    />
                )}
            </div>

            {viewerSource && (
                <DocumentViewerModal
                    documentId={viewerSource.documentId}
                    filename={viewerSource.filename}
                    chunkContent={viewerSource.content}
                    isOpen={!!viewerSource}
                    onClose={() => setViewerSource(null)}
                />
            )}
        </div>
    );
}
